import { Component, ErrorInfo, ReactNode } from 'react';
import { clearUserSession } from '../utils/sessionManager';
import { isTokenError } from '../utils/tokenErrorHandler';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Check if the error is token-related
    if (error.message && isTokenError(error.message)) {
      clearUserSession();
      return;
    }

    // For other errors, you might want to log them to a service
    // logErrorToService(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Check if it's a token error
      if (this.state.error && isTokenError(this.state.error.message)) {
        // Don't render anything for token errors as the user will be redirected
        return null;
      }

      // For other errors, show a fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Something went wrong
              </h2>
              <p className="text-gray-600 mb-6">
                An unexpected error occurred. Please try refreshing the page.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 